// AUPRÉA — Écran commissions v2 (Étape 7)
import { useState, useEffect } from "react";
import axios from "axios";
import { C, Btn, Alert, Spinner, authDownload, fmtEur } from "./shared";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const ax = axios.create({ baseURL: API });
ax.interceptors.request.use((c) => {
  const tok = localStorage.getItem("auprea_token");
  if (tok) c.headers.Authorization = `Bearer ${tok}`;
  return c;
});

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };
const moisLabel = (yyyy_mm) => {
  const [y, m] = yyyy_mm.split("-");
  return new Date(y, parseInt(m) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };
const prevMonth = (yyyy_mm) => {
  const [y, m] = yyyy_mm.split("-").map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
};

// ── KPI card ────────────────────────────────────────────────────────────────
const Kpi = ({ label, value, delta, color }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 16, borderTop: `3px solid ${color || C.gold}` }}>
    <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginTop: 6 }}>{value}</div>
    {delta && <div style={{ fontSize: 11, color: C.mid, marginTop: 4 }}>{delta}</div>}
  </div>
);

// ── Vue Consultant (Étape 17) ───────────────────────────────────────────────
const STAT_COLOR = { potentielle: C.muted, en_attente: C.amber, a_valider: C.purp, due: C.teal, versee: C.green, annulee: C.red };
const StatutPill = ({ s, label }) => (
  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, color: STAT_COLOR[s] || C.mid, background: `${STAT_COLOR[s] || C.mid}18`, whiteSpace: "nowrap" }}>{label || s}</span>
);
const CARTES = [["potentielles", "Potentielles", C.muted, "estimées, jamais versées"], ["en_attente", "En attente", C.amber, "rétractation en cours"],
  ["a_valider", "À valider", C.purp, "confirmation AUPRÉA"], ["dues", "Dues ce mois", C.teal, "versées avec le relevé"], ["versees_annee", "Versées", C.green, "cumul de l'année"]];
const thS = { padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", background: C.gray2, whiteSpace: "nowrap" };
const tdS = { padding: "9px 12px", fontSize: 12, borderTop: `.5px solid ${C.border}` };
const Table = ({ cols, rows, testid, empty }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, overflowX: "auto" }} data-testid={testid}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{cols.map(c => <th key={c[1]} style={{ ...thS, textAlign: c[2] ? "right" : "left" }}>{c[1]}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c[1]} style={{ ...tdS, textAlign: c[2] ? "right" : "left" }}>{c[0](r)}</td>)}</tr>)}
        {!rows.length && <tr><td colSpan={cols.length} style={{ ...tdS, textAlign: "center", color: C.muted, padding: 24 }}>{empty || "Aucune donnée"}</td></tr>}
      </tbody>
    </table>
  </div>
);
const pct = (t) => (t || t === 0 ? `${Math.round(t * 1000) / 10} %` : "—");

const PalierBar = () => {
  const [p, setP] = useState(null);
  useEffect(() => { ax.get("/consultant/palier-info").then(r => setP(r.data)).catch(() => {}); }, []);
  if (!p || p.role === "admin") return null;
  const cible = p.prochain_palier ? (p.prochain_palier === "Confirmé" ? p.seuil_confirme - 1 : p.seuil_expert - 1) : p.nb_ventes_mois;
  const w = cible ? Math.min(100, Math.round(100 * p.nb_ventes_mois / cible)) : 100;
  return (
    <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 16 }} data-testid="commissions-palier">
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.mid, marginBottom: 6 }}>
        <span><strong style={{ color: C.navy }}>{p.nb_ventes_mois} vente(s)</strong> ce mois · prochaine vente au palier <strong>{p.palier_actuel}</strong> ({pct(p.taux_actuel)})</span>
        <span>{p.prochain_palier ? `Encore ${p.encore_avant_prochain - 1 > 0 ? p.encore_avant_prochain - 1 : 0} vente(s) avant ${p.prochain_palier}` : "Palier maximum atteint"}</span>
      </div>
      <div style={{ height: 8, background: C.gray2, borderRadius: 6, overflow: "hidden" }}><div style={{ width: `${w}%`, height: "100%", background: C.gold }} /></div>
    </div>
  );
};

const Releves = () => {
  const [rel, setRel] = useState([]);
  useEffect(() => { ax.get("/commissions/releves").then(r => setRel(r.data)).catch(() => {}); }, []);
  if (!rel.length) return null;
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Mes relevés</div>
      <Table testid="mes-releves" rows={rel} cols={[[r => r.numero, "Relevé"], [r => moisLabel(r.mois), "Mois"], [r => fmtEur(r.total), "Total", 1],
        [r => r.statut === "verse" ? `Versé le ${fmtDate(r.verse_le)}` : "Émis", "Statut"],
        [r => <Btn small onClick={() => authDownload(`/commissions/releves/${r.id}/pdf`, `${r.numero}.pdf`, true)} data-testid={`releve-pdf-${r.id}`}>PDF</Btn>, ""]]} />
    </div>
  );
};

export const MesCommissionsV2 = ({ onOpenLead }) => {
  const [mois, setMois] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("client");
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null);
    ax.get("/commissions/synthese", { params: { mois } }).then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Erreur"));
  }, [mois]);
  if (err) return <Alert type="error"><span>⚠</span><span>{err}</span></Alert>;
  if (!data) return <Spinner />;
  const cur = currentMonth();
  const vide = !data.par_client.length && !data.equipe.n1.length && !data.equipe.n2_n3.length;
  return (
    <div data-testid="mes-commissions">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", textTransform: "capitalize" }}>{moisLabel(mois)}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn small variant={mois !== cur ? "navy" : undefined} onClick={() => setMois(prevMonth(cur))} data-testid="commissions-mois-precedent">Mois précédent</Btn>
          <Btn small variant={mois === cur ? "navy" : undefined} onClick={() => setMois(cur)} data-testid="commissions-mois-courant">Mois en cours</Btn>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10, marginBottom: 16 }}>
        {CARTES.map(([k, l, col, sub]) => (
          <div key={k} data-testid={`carte-${k}`} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderTop: `3px solid ${col}`, borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}>{l}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginTop: 4 }} data-testid={`carte-${k}-montant`}>{fmtEur(data.cartes[k].montant)}</div>
            <div style={{ fontSize: 11, color: C.mid }}>{data.cartes[k].nombre} · {sub}</div>
          </div>
        ))}
      </div>
      <PalierBar />
      {data.en_attente.length > 0 && (
        <Alert type="warn"><span>⏳</span><span data-testid="commissions-en-attente-liste">{data.en_attente.map((e, i) => <span key={i} style={{ marginRight: 12 }}>{e.client} : {fmtEur(e.montant)} — fin de rétractation le {fmtDate(e.due_at)}</span>)}</span></Alert>
      )}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[["client", "Par client"], ["prestation", "Par prestation"], ["equipe", "Mon équipe"], ["mois", "Par mois"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`commissions-tab-${k}`} style={{ padding: "6px 14px", borderRadius: 20, border: `.5px solid ${C.border}`, background: tab === k ? C.navy : "#fff", color: tab === k ? "#fff" : C.mid, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
        ))}
      </div>
      {vide && tab !== "mois" ? (
        <div data-testid="commissions-vide" style={{ background: "#fff", border: `.5px dashed ${C.border}`, borderRadius: 10, padding: 30, textAlign: "center", color: C.mid, fontSize: 13 }}>
          Aucune commission ce mois-ci. Vos commissions apparaissent dès qu'une proposition est envoyée (Potentielle), puis évoluent jusqu'au versement.
        </div>
      ) : (
        <>
          {tab === "client" && <Table testid="commissions-par-client" rows={data.par_client} cols={[
            [r => <strong style={{ color: C.navy }}>{r.client || "—"}</strong>, "Client"], [r => (r.prestations || []).join(", ") + (r.abonnement ? " (mensualité)" : ""), "Prestation(s)"],
            [r => r.statut === "potentielle" ? "—" : fmtEur(r.encaisse), "Encaissé", 1], [r => `${pct(r.taux)} · ${r.palier || ""}`, "Taux / palier"],
            [r => <strong>{fmtEur(r.commission)}</strong>, "Commission", 1], [r => <StatutPill s={r.statut} label={r.statut_label} />, "Statut"],
            [r => r.leadId && onOpenLead ? <Btn small onClick={() => onOpenLead(r.leadId)} data-testid={`commission-lead-${r.id}`}>Lead</Btn> : "", ""]]} />}
          {tab === "prestation" && <Table testid="commissions-par-prestation" rows={data.par_prestation} cols={[[r => r.prestation, "Prestation"], [r => r.ventes, "Ventes", 1], [r => fmtEur(r.encaisse), "Encaissé", 1], [r => fmtEur(r.commission), "Commission", 1]]} />}
          {tab === "equipe" && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, margin: "4px 0 8px" }}>N1 — mes filleuls directs</div>
              <Table testid="commissions-equipe-n1" rows={data.equipe.n1} empty="Aucune vente de vos filleuls ce mois-ci." cols={[[r => r.consultant, "Consultant"], [r => r.ventes, "Ventes", 1], [r => r.prestations.join(", "), "Prestations vendues"], [r => fmtEur(r.encaisse), "Encaissé", 1], [r => <strong>{fmtEur(r.commission)}</strong>, "Ma commission", 1], [r => r.statuts.join(", "), "Statut"]]} />
              <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, margin: "16px 0 8px" }}>N2 et N3 — total par consultant</div>
              <Table testid="commissions-equipe-n23" rows={data.equipe.n2_n3} empty="Aucune commission N2/N3 ce mois-ci." cols={[[r => r.consultant, "Consultant"], [r => `N${r.niveau}`, "Niveau"], [r => fmtEur(r.commission), "Ma commission", 1]]} />
            </>
          )}
        </>
      )}
      {tab === "mois" && <Table testid="commissions-par-mois" rows={[...data.par_mois].reverse()} cols={[[r => <span style={{ textTransform: "capitalize" }}>{moisLabel(r.mois)}</span>, "Mois"], [r => fmtEur(r.directes), "Directes", 1], [r => fmtEur(r.abonnements), "Abonnements", 1], [r => fmtEur(r.equipe), "Équipe", 1], [r => <strong>{fmtEur(r.total)}</strong>, "Total", 1]]} />}
      <Releves />
    </div>
  );
};

export const CommissionsAdminV2 = () => {
  const [mois, setMois] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [validating, setValidating] = useState(false);
  const reload = () => {
    setLoading(true);
    ax.get(`/commissions/v2/mois/${mois}/admin`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [mois]); // eslint-disable-line

  const validerMois = async (conseillerId) => {
    if (!window.confirm(`Valider et figer le mois ${mois}${conseillerId ? " pour ce consultant" : " pour tous"} ?`)) return;
    setValidating(true);
    try { await ax.post("/commissions/v2/valider-mois", { mois, conseillerId }); reload(); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setValidating(false); }
  };

  const exportCsv = async () => {
    const tok = localStorage.getItem("auprea_token");
    const r = await axios.get(`${API}/commissions/v2/export-csv/${mois}`, { headers: { Authorization: `Bearer ${tok}` }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `commissions_${mois}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <Spinner />;
  if (err) return <Alert type="error"><span>⚠</span><span>{err}</span></Alert>;
  const total = data.rows.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>Commissions · {moisLabel(mois)}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn small onClick={() => setMois(prevMonth(mois))} data-testid="admin-com-prev">← Mois précédent</Btn>
          <Btn small onClick={() => setMois(currentMonth())} data-testid="admin-com-current">Mois en cours</Btn>
          <Btn small onClick={exportCsv} data-testid="admin-com-csv">📥 Export CSV</Btn>
          <Btn small variant="gold" onClick={() => validerMois(null)} disabled={validating} data-testid="admin-com-valider-all">✓ Valider tout le mois</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <Kpi label="Total à verser" value={fmtEur(total)} color={C.gold} />
        <Kpi label="Consultants" value={data.rows.length} color={C.teal} />
      </div>

      {data.rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted, background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}` }}>Aucune commission ce mois.</div>
      ) : (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: C.gray2 }}>
              {["Consultant","Encaissé","Directe","Équipe","Primes PR","Total","Actions"].map(h => <th key={h} style={{ padding: "10px 14px", textAlign: h === "Actions" ? "right" : "left", fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.conseillerId} style={{ borderTop: `.5px solid ${C.border}` }} data-testid={`admin-row-${r.conseillerId}`}>
                  <td style={{ padding: "10px 14px", color: C.navy, fontWeight: 600 }}>
                    {r.beneficiaireNom}
                    {r.verrouille && <span style={{ background: C.greenlt, color: C.green, marginLeft: 8, padding: "2px 8px", borderRadius: 10, fontSize: 9, fontWeight: 700 }}>VALIDÉ</span>}
                  </td>
                  <td style={{ padding: "10px 14px", color: C.mid }}>{fmtEur(r.encaisse)}</td>
                  <td style={{ padding: "10px 14px", color: C.mid }}>{fmtEur(r.directe)}</td>
                  <td style={{ padding: "10px 14px", color: C.mid }}>{fmtEur(r.override)}</td>
                  <td style={{ padding: "10px 14px", color: C.mid }}>{fmtEur(r.prime_pr + r.prime_pr_cotisation)}</td>
                  <td style={{ padding: "10px 14px", color: C.navy, fontWeight: 700 }}>{fmtEur(r.total)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>
                    {!r.verrouille && <Btn small variant="gold" onClick={() => validerMois(r.conseillerId)} data-testid={`admin-valider-${r.conseillerId}`}>Valider</Btn>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
